import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface ModernSearchSelectOption {
  value: any;
  label: string;
  search?: string;
  triggerLabel?: string;
  code?: string;
  codePosition?: 'start' | 'end';
  flagUrl?: string;
}

@Component({
  selector: 'app-modern-search-select',
  templateUrl: './modern-search-select.component.html',
  styleUrls: ['./modern-search-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ModernSearchSelectComponent),
    multi: true,
  }],
})
export class ModernSearchSelectComponent implements ControlValueAccessor {
  @Input() options: ModernSearchSelectOption[] = [];
  @Input() searchPlaceholder = 'Buscar...';
  @Input() emptyText = 'Nenhum resultado encontrado';
  @Input() placeholder = 'Selecione';
  @Input() ariaLabel = 'Selecionar opção';

  @ViewChild('searchInput', {static: false}) searchInput: ElementRef<HTMLInputElement>;

  disabled = false;
  isOpen = false;
  query = '';
  value: any = null;

  private onChange: (value: any) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  get selectedOption(): ModernSearchSelectOption {
    return this.options.find(option => String(option.value) === String(this.value));
  }

  get filteredOptions(): ModernSearchSelectOption[] {
    const query = this.normalize(this.query);
    if (!query) return this.options;

    return this.options.filter(option => {
      const searchable = option.search || `${option.label} ${option.code || ''} ${option.triggerLabel || ''}`;
      return this.normalize(searchable).includes(query);
    });
  }

  toggle(): void {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.query = '';
      setTimeout(() => {
        if (this.searchInput) this.searchInput.nativeElement.focus();
      });
    } else {
      this.onTouched();
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.query = '';
    this.onTouched();
  }

  select(option: ModernSearchSelectOption): void {
    this.value = option.value;
    this.onChange(option.value);
    this.close();
  }

  isSelected(option: ModernSearchSelectOption): boolean {
    return String(option.value) === String(this.value);
  }

  updateQuery(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  private normalize(value: string): string {
    return (value || '')
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }
}
